/**
 * Bill - 3D Bill Representation
 *
 * Features:
 * - PlaneGeometry representing a bill (1.2 x 0.6 units)
 * - Custom shader with bend effect for realistic physics
 * - Denomination-based coloring
 * - Smooth animations
 */

import * as THREE from 'three';
import * as CANNON from 'cannon-es';
import { getDenominationColor, formatDenomination } from './BillDenominations';
import type { AssetLoader } from '../../engine/loaders/AssetLoader';
import { CashAnims } from '../config/AnimationConfig';
import { renderScheduler } from '../../engine/render/RenderScheduler';

export interface BillConfig {
  denomination: number;
  position?: { x: number; y: number; z: number };
  segments?: number;
  assetLoader?: AssetLoader;
  /** Whether the bill casts a shadow (off on the 'low' quality tier). */
  shadows?: boolean;
  /** Target texture resolution in px (256/512/1024) → picks CDN small/medium/large. */
  textureSize?: number;
}

// Bill dimensions (will be calculated from texture aspect ratio)
let BILL_WIDTH = 1.8;  // Default
let BILL_HEIGHT = 0.9; // Default
let aspectRatioCalculated = false;

// Shared currency-note texture cache. A bill's texture is identical for every
// bill of the same denomination+variant, so we upload exactly ONE GPU texture
// per key and share it across all bills. Previously each Bill uploaded its own
// copy via a fresh TextureLoader — the betting↔shooting cycle churns many bills,
// and on iOS/WKWebView's tight GPU budget those duplicate uploads piled up until
// the GPU process crashed and the page reloaded ("app restarts" on betting
// entry). The cache is bounded by the number of distinct denominations (a
// handful), so it's safe to keep for the whole session. Shared textures are
// therefore NEVER disposed per-bill (see Bill.dispose); only disposeTextureCache
// frees them. We cache the Promise so concurrent bills of the same denomination
// dedupe onto a single in-flight load.
const billTextureCache = new Map<string, Promise<THREE.Texture>>();

function loadSharedBillTexture(key: string, load: () => Promise<THREE.Texture>): Promise<THREE.Texture> {
  let p = billTextureCache.get(key);
  if (!p) {
    p = load().catch((err) => {
      billTextureCache.delete(key); // don't cache a failed load
      throw err;
    });
    billTextureCache.set(key, p);
  }
  return p;
}

export class Bill {
  private denomination: number;
  private mesh: THREE.Mesh;
  private material: THREE.MeshStandardMaterial;
  private bendAmount = 0;
  private targetBend = 0;
  private segments: number;
  private shadows: boolean;
  private textureSize: number;
  private assetLoader?: AssetLoader;
  private texture?: THREE.Texture;
  private textureShared = false;   // texture is cache-owned; never dispose per-bill
  private disposed = false;        // guards the async loadTexture race
  private originalPositions: Float32Array | null = null;
  private originalNormals: Float32Array | null = null;

  // Animation properties
  private targetPosition: THREE.Vector3;
  private targetRotation: THREE.Euler;
  private animating = false;

  // Bend mode: 'asymmetric' (left only) or 'symmetric' (both edges)
  private bendMode: 'asymmetric' | 'symmetric' = 'asymmetric';

  // Track last applied bend to avoid redundant geometry updates
  private lastAppliedBend = 0;
  private lastAppliedBendMode: 'asymmetric' | 'symmetric' = 'asymmetric';

  // Physics properties
  private body: CANNON.Body | null = null;
  private physicsEnabled = false;
  public onSettled?: () => void;

  // ── Object pool ───────────────────────────────────────────────
  // App-level, per-denomination free list. Bills are NEVER truly freed during
  // a session (not even when leaving a room) — dispose() resets + parks them
  // here, and acquire() reuses a parked bill of the same denomination. This
  // kills the per-round create/dispose GPU churn (geometry + material + the
  // compiled shader program are all reused, so nothing recompiles or reallocs)
  // that fragmented the WKWebView GPU budget and blacked the scene out. Keyed by
  // denomination so the cache-shared texture already matches on reuse.
  private static pool = new Map<number, Bill[]>();
  private pooled = false;

  /** Reuse a parked bill of this denomination, or build a fresh one. */
  static acquire(config: BillConfig): Bill {
    const list = Bill.pool.get(config.denomination);
    const reused = list && list.length ? list.pop()! : null;
    if (reused) {
      reused.pooled = false;
      reused.reset();
      if (config.position) reused.setPosition(config.position);
      return reused;
    }
    return new Bill(config);
  }

  constructor(config: BillConfig) {
    this.denomination = config.denomination;
    this.segments = config.segments ?? 8;
    this.shadows = config.shadows ?? true;
    this.textureSize = config.textureSize ?? 1024;
    this.assetLoader = config.assetLoader;

    const geometry = this.createGeometry();
    // Use simple material for testing instead of complex shader
    this.material = this.createSimpleMaterial();
    this.mesh = new THREE.Mesh(geometry, this.material);
    // Back-ref for the stray-bill sweep (clearAllBettingObjects): lets a
    // scene-level pass recover the Bill instance from an orphaned mesh and
    // park it properly instead of leaking it on the table.
    this.mesh.userData.sdlBill = this;

    // Store original vertex positions and normals for bending
    const posAttr = geometry.getAttribute('position');
    this.originalPositions = new Float32Array(posAttr.array.length);
    this.originalPositions.set(posAttr.array);

    const normAttr = geometry.getAttribute('normal');
    this.originalNormals = new Float32Array(normAttr.array.length);
    this.originalNormals.set(normAttr.array);

    // Load texture if assetLoader is provided
    if (this.assetLoader) {
      this.loadTexture();
    }

    // Set initial position
    if (config.position) {
      this.mesh.position.set(
        config.position.x,
        config.position.y,
        config.position.z
      );
    }

    this.targetPosition = this.mesh.position.clone();
    this.targetRotation = this.mesh.rotation.clone();

    this.mesh.castShadow = this.shadows;
    this.mesh.receiveShadow = false;
  }

  /**
   * Create bill geometry (segmented plane for bending)
   */
  private createGeometry(): THREE.PlaneGeometry {
    return new THREE.PlaneGeometry(
      BILL_WIDTH,
      BILL_HEIGHT,
      this.segments,
      this.segments
    );
  }

  /**
   * Get texture filename for denomination
   * If exact match not found, use closest available texture
   */
  /**
   * Load texture for this bill.
   * Uses CDN URL from server currency notes if available,
   * falls back to local texture files.
   */
  private async loadTexture(): Promise<void> {
    if (!this.assetLoader) return;

    // Try CDN URL from server notes first. Pick the CDN variant from the
    // quality tier's target texture size (low=small, medium=medium, high=large).
    const { getNoteCdnUrl } = await import('../../api/currencyNoteApi');
    const cdnSize = this.textureSize <= 256 ? 'small' : this.textureSize <= 512 ? 'medium' : 'large';
    const cdnUrl = getNoteCdnUrl(this.denomination, cdnSize);

    // Resolve the source + a stable cache key so every bill of this denomination
    // shares ONE GPU texture (see billTextureCache above). CDN textures are
    // landscape (1024x495); local fallbacks are portrait (512x1024).
    const assetLoader = this.assetLoader;
    const isLandscape = cdnUrl != null;
    let cacheKey: string;
    let load: () => Promise<THREE.Texture>;
    if (cdnUrl) {
      cacheKey = `cdn:${cdnUrl}`;
      load = () => new Promise<THREE.Texture>((resolve, reject) => {
        new THREE.TextureLoader().load(cdnUrl, resolve, undefined, reject);
      });
    } else {
      // Fallback: local texture files (closest available denomination).
      const availableTextures = [
        { denom: 500, file: 'bill_500.png' },
        { denom: 10000, file: 'bill_10000.png' },
        { denom: 1000000, file: 'bill_1000000.png' },
        { denom: 500000000, file: 'bill_500000000.png' },
      ];
      let match = availableTextures.find(t => t.denom === this.denomination);
      if (!match) {
        match = availableTextures[0];
        let minDiff = Math.abs(this.denomination - match.denom);
        for (const t of availableTextures) {
          const diff = Math.abs(this.denomination - t.denom);
          if (diff < minDiff) { minDiff = diff; match = t; }
        }
      }
      const file = match.file;
      cacheKey = `local:${file}`;
      load = () => assetLoader.loadTexture(`textures/bills/${file}`);
    }

    try {
      const texture = await loadSharedBillTexture(cacheKey, load);

      // Prepare the shared texture's sampling/orientation once per cache entry.
      if (!texture.userData.sdlBillPrepared) {
        texture.colorSpace = THREE.SRGBColorSpace;
        texture.minFilter = THREE.LinearFilter;
        texture.magFilter = THREE.LinearFilter;
        if (!isLandscape) {
          // Rotate portrait texture 90 degrees for landscape geometry.
          texture.center.set(0.5, 0.5);
          texture.rotation = Math.PI / 2;
        }
        texture.needsUpdate = true;
        texture.userData.sdlBillPrepared = true;
      }

      // Calculate aspect ratio from texture and update bill dimensions (only once)
      if (!aspectRatioCalculated) {
        const img = texture.image as HTMLImageElement;
        const textureWidth = img.width;
        const textureHeight = img.height;

        let aspectRatio: number;
        if (isLandscape) {
          aspectRatio = textureWidth / textureHeight; // Already landscape
        } else {
          aspectRatio = textureHeight / textureWidth; // Portrait rotated to landscape
        }

        BILL_WIDTH = 1.8;
        BILL_HEIGHT = BILL_WIDTH / aspectRatio;

        aspectRatioCalculated = true;

        this.mesh.geometry.dispose();
        this.mesh.geometry = this.createGeometry();

        const newPosAttr = this.mesh.geometry.getAttribute('position');
        this.originalPositions = new Float32Array(newPosAttr.array.length);
        this.originalPositions.set(newPosAttr.array);

        const newNormAttr = this.mesh.geometry.getAttribute('normal');
        this.originalNormals = new Float32Array(newNormAttr.array.length);
        this.originalNormals.set(newNormAttr.array);
      }

      // If the bill was disposed while the texture load was in flight, don't
      // touch its dead material/mesh. The texture stays cached + shared.
      if (this.disposed) return;

      this.material.map = texture;
      this.material.color.setHex(0xffffff);
      this.material.emissive.setHex(0x000000);
      this.material.emissiveIntensity = 0;
      this.material.roughness = 1.0;
      this.material.metalness = 0.0;
      this.material.needsUpdate = true;

      // Cache-owned: do NOT dispose this texture in dispose() — it's shared by
      // every bill of this denomination.
      this.texture = texture;
      this.textureShared = true;
    } catch (error) {
      console.warn(`⚠️ Failed to load texture for $${this.denomination} bill, using color fallback`, error);
    }
  }

  /**
   * Create physics body for bill
   */
  createPhysicsBody(world: CANNON.World, material: CANNON.Material): void {
    // Shape defined in local space - bill will be rotated -90° on X to lay flat
    // After rotation: local Y becomes world Z, local Z becomes world Y
    const shape = new CANNON.Box(new CANNON.Vec3(
      BILL_WIDTH / 2,   // X: width (left-right, unchanged)
      BILL_HEIGHT / 2,  // Y: height (becomes Z/forward-back after rotation)
      0.15              // Z: thickness (becomes Y/up-down after rotation - 15cm box)
    ));

    this.body = new CANNON.Body({
      mass: 0.1,       // Even heavier for better collision (100 grams)
      shape,
      material,
      linearDamping: 0.3,   // Lower air resistance for more sliding
      angularDamping: 0.4,  // Lower rotation dampening for more tumbling
      sleepSpeedLimit: 0.2, // Lower threshold for sleeping (settle quicker)
      sleepTimeLimit: 0.5,  // More time before sleeping (allow settling)
      collisionResponse: true, // Enable collision response
      type: CANNON.Body.DYNAMIC, // Explicitly set as dynamic
      // Collision groups: Bills collide with TABLE, WALL, and other BILLS
      collisionFilterGroup: 1 << 3,  // BILL group
      collisionFilterMask: (1 << 1) | (1 << 2) | (1 << 3),  // Collide with TABLE, WALL, and BILL
    });

    // Set initial position/rotation from mesh
    this.body.position.set(
      this.mesh.position.x,
      this.mesh.position.y,
      this.mesh.position.z
    );
    this.body.quaternion.set(
      this.mesh.quaternion.x,
      this.mesh.quaternion.y,
      this.mesh.quaternion.z,
      this.mesh.quaternion.w
    );

    world.addBody(this.body);
    this.physicsEnabled = true;
  }

  /**
   * Get physics body (for applying forces, velocities, etc)
   */
  getBody(): CANNON.Body | null {
    return this.body;
  }

  /**
   * Create simple colored material (for testing - easier to see)
   */
  private createSimpleMaterial(): THREE.MeshStandardMaterial {
    const color = getDenominationColor(this.denomination);

    return new THREE.MeshStandardMaterial({
      color: color,
      roughness: 1.0,   // Paper is fully rough — no specular
      metalness: 0.0,    // Paper is not metallic
      emissive: color,
      emissiveIntensity: 0.3,
      side: THREE.DoubleSide,
      shadowSide: THREE.DoubleSide, // Both sides cast shadows — needed for angled/tilted bills during drag
    });
  }

  /**
   * Set bend amount (0 = flat, 1 = fully bent)
   */
  setBend(amount: number): void {
    this.targetBend = THREE.MathUtils.clamp(amount, -2, 2);
  }

  /**
   * Set bend immediately (no animation)
   */
  setBendImmediate(amount: number): void {
    this.bendAmount = THREE.MathUtils.clamp(amount, -2, 2);
    this.targetBend = this.bendAmount;
    this.applyBendToGeometry();
    renderScheduler.markShadowsDirty();
  }

  /**
   * Set bend mode
   * - 'asymmetric': only left edge curls (for settled bills)
   * - 'symmetric': both edges curl (for lift animation - air resistance effect)
   */
  setBendMode(mode: 'asymmetric' | 'symmetric'): void {
    this.bendMode = mode;
    this.applyBendToGeometry();
  }

  /**
   * Apply bend deformation to geometry vertices
   * Supports both asymmetric (left only) and symmetric (both edges) modes
   * Optimized: skips if bend hasn't changed significantly
   */
  private applyBendToGeometry(): void {
    if (!this.originalPositions) return;

    // Skip if bend hasn't changed significantly (within 0.01 threshold)
    const bendDelta = Math.abs(this.bendAmount - this.lastAppliedBend);
    const modeChanged = this.bendMode !== this.lastAppliedBendMode;

    if (bendDelta < 0.01 && !modeChanged) {
      return;
    }

    this.lastAppliedBend = this.bendAmount;
    this.lastAppliedBendMode = this.bendMode;

    const geometry = this.mesh.geometry as THREE.PlaneGeometry;
    const posAttr = geometry.getAttribute('position');
    const positions = posAttr.array as Float32Array;

    const bendStrength = this.bendAmount * CashAnims.bendStrengthMultiplier;
    const halfWidth = BILL_WIDTH / 2;

    for (let i = 0; i < positions.length; i += 3) {
      const origX = this.originalPositions[i];
      const origZ = this.originalPositions[i + 2];

      const normalizedX = origX / halfWidth; // -1 to 1
      let bendOffset = 0;

      if (this.bendMode === 'symmetric') {
        // Symmetric bend - both edges curl (air resistance effect)
        const absNormX = Math.abs(normalizedX);
        if (absNormX > 0.25) {
          const bendProgress = (absNormX - 0.25) / 0.75;
          const expBend = bendProgress * bendProgress * Math.sqrt(bendProgress); // ~pow 2.5
          bendOffset = expBend * bendStrength;
        }
      } else {
        // Asymmetric bend - only affect left side (negative X)
        if (normalizedX < -0.25) {
          const bendProgress = (-normalizedX - 0.25) / 0.75;
          const expBend = bendProgress * bendProgress * Math.sqrt(bendProgress); // ~pow 2.5
          bendOffset = expBend * bendStrength;
        }
      }

      positions[i + 2] = origZ - bendOffset;
    }

    posAttr.needsUpdate = true;

    // Restore original flat normals instead of recomputing.
    // computeVertexNormals() on a segmented plane creates faceted normals
    // that produce harsh grid-like specular artifacts under spotlights.
    // Paper/bills don't create faceted reflections when slightly bent,
    // so flat normals are both more realistic and cheaper (array copy vs
    // cross-product computation per face).
    if (this.originalNormals) {
      const normAttr = geometry.getAttribute('normal');
      (normAttr.array as Float32Array).set(this.originalNormals);
      normAttr.needsUpdate = true;
    }
  }

  /**
   * Update (call every frame)
   */
  update(deltaTime: number): void {
    // Update time uniform for animations (only for shader material)
    if (this.material instanceof THREE.ShaderMaterial && this.material.uniforms.uTime) {
      this.material.uniforms.uTime.value += deltaTime;
    }

    // Animate bend toward target
    if (Math.abs(this.bendAmount - this.targetBend) > 0.001) {
      const bendLerp = CashAnims.bendLerpSpeed * deltaTime;
      this.bendAmount += (this.targetBend - this.bendAmount) * bendLerp;
      this.applyBendToGeometry();
      // Bend changes the bill's silhouette — its shadow must follow.
      renderScheduler.markShadowsDirty();
    }

    // If physics enabled, sync mesh from physics body (like dice - no interpolation)
    if (this.physicsEnabled && this.body) {
      this.mesh.position.copy(this.body.position as any);
      this.mesh.quaternion.copy(this.body.quaternion as any);

      // Safety check: if bill falls below ground, force it back up
      if (this.body.position.y < 0.05) {
        console.error(`⚠️ Bill $${this.denomination} fell below ground! Y = ${this.body.position.y.toFixed(2)}, Velocity: ${this.body.velocity.y.toFixed(2)}`);
        // Force to table surface
        this.body.position.y = 0.1;
        this.body.velocity.y = Math.max(0, this.body.velocity.y); // Stop downward motion
        this.body.wakeUp();
      }

      // Check if settled (sleeping)
      if (this.body.sleepState === CANNON.Body.SLEEPING) {
        if (this.onSettled) {
          this.onSettled();
          this.onSettled = undefined; // Call only once
        }
      }
    }
    // Otherwise use lerp animation for non-physics bills
    else if (this.animating) {
      // Per-frame lerp (not gsap/physics) — keep rendering while it moves,
      // and re-render the shadow map (bills cast shadows).
      renderScheduler.pulse();
      renderScheduler.markShadowsDirty();
      const lerpFactor = CashAnims.positionLerpSpeed * deltaTime;

      this.mesh.position.lerp(this.targetPosition, lerpFactor);
      this.mesh.rotation.x += (this.targetRotation.x - this.mesh.rotation.x) * lerpFactor;
      this.mesh.rotation.y += (this.targetRotation.y - this.mesh.rotation.y) * lerpFactor;
      this.mesh.rotation.z += (this.targetRotation.z - this.mesh.rotation.z) * lerpFactor;

      // Check if reached target
      const distance = this.mesh.position.distanceTo(this.targetPosition);
      if (distance < 0.01) {
        this.animating = false;
      }
    }
  }

  /**
   * Animate bill to a target position and rotation
   */
  animateTo(
    position: { x: number; y: number; z: number },
    rotation?: { x: number; y: number; z: number }
  ): void {
    this.targetPosition.set(position.x, position.y, position.z);

    if (rotation) {
      this.targetRotation.set(rotation.x, rotation.y, rotation.z);
    }

    this.animating = true;
  }

  /**
   * Set position immediately (no animation)
   */
  setPosition(position: { x: number; y: number; z: number }): void {
    this.mesh.position.set(position.x, position.y, position.z);
    this.targetPosition.copy(this.mesh.position);
    this.animating = false;
    // Chokepoint for all non-physics bill motion (drags, GSAP flights,
    // placements) — bills cast shadows, so the map must follow.
    renderScheduler.markShadowsDirty();
  }

  /**
   * Set rotation immediately (no animation)
   */
  setRotation(rotation: { x: number; y: number; z: number }): void {
    this.mesh.rotation.set(rotation.x, rotation.y, rotation.z);
    this.targetRotation.copy(this.mesh.rotation);
    renderScheduler.markShadowsDirty();
  }

  /**
   * Get current position
   */
  getPosition(): THREE.Vector3 {
    return this.mesh.position.clone();
  }

  /**
   * Get current rotation
   */
  getRotation(): THREE.Euler {
    return this.mesh.rotation.clone();
  }

  /**
   * Get denomination
   */
  getDenomination(): number {
    return this.denomination;
  }

  /**
   * Get formatted denomination string
   */
  getFormattedDenomination(): string {
    return formatDenomination(this.denomination);
  }

  /**
   * Get Three.js mesh
   */
  getMesh(): THREE.Mesh {
    return this.mesh;
  }

  /**
   * Check if currently animating
   */
  isAnimating(): boolean {
    return this.animating;
  }

  /**
   * Dispose of resources
   */
  /**
   * Recycle this bill into the pool instead of freeing its GPU resources. Named
   * dispose() because all the existing call sites (round-end teardown, undo,
   * fade-settle, zone clears, full betting teardown) already call it at exactly
   * the right moments — they now park instead of destroy. Idempotent: a bill
   * already parked is ignored (guards double-release → the same bill being
   * handed out twice). To truly free GPU memory (app exit only) use destroy().
   */
  dispose(): void {
    if (this.pooled) return;
    this.reset();
    this.pooled = true;
    let list = Bill.pool.get(this.denomination);
    if (!list) { list = []; Bill.pool.set(this.denomination, list); }
    list.push(this);
  }

  /**
   * Restore all transient state so a parked bill can be reused as if freshly
   * constructed (same denomination → same cached texture, zero realloc). Flattens
   * the bent geometry, clears transform/animation/physics/callback state, detaches
   * the mesh from any scene/group, and re-shows it. Does NOT touch geometry/
   * material/texture objects — the pool keeps them alive for the whole session.
   */
  reset(): void {
    // Geometry: bending mutates the position/normal attributes in place, so
    // restore them from the pristine snapshot captured at construction.
    if (this.originalPositions && this.originalNormals) {
      const posAttr = this.mesh.geometry.getAttribute('position');
      (posAttr.array as Float32Array).set(this.originalPositions);
      posAttr.needsUpdate = true;
      const normAttr = this.mesh.geometry.getAttribute('normal');
      (normAttr.array as Float32Array).set(this.originalNormals);
      normAttr.needsUpdate = true;
    }
    this.bendAmount = 0;
    this.targetBend = 0;
    this.lastAppliedBend = 0;
    this.bendMode = 'asymmetric';
    this.lastAppliedBendMode = 'asymmetric';

    // Transform + animation
    this.mesh.position.set(0, 0, 0);
    this.mesh.rotation.set(0, 0, 0);
    this.mesh.scale.set(1, 1, 1);
    this.mesh.visible = true;
    this.targetPosition.copy(this.mesh.position);
    this.targetRotation.copy(this.mesh.rotation);
    this.animating = false;

    // Per-use callback (set by drop animations) must not survive to the next use.
    this.onSettled = undefined;

    // Highlight state (Wallet.applyBillHighlight mutates emissiveIntensity) back
    // to the texture-loaded default (0 once the note image is on, else the 0.3
    // pre-load colour glow).
    this.material.emissiveIntensity = this.texture ? 0 : 0.3;

    // Physics: remove any body from the world so it stops simulating.
    if (this.body) {
      this.body.world?.removeBody(this.body);
      this.body = null;
      this.physicsEnabled = false;
    }

    // Detach from whatever scene/group held it so a parked bill never renders.
    this.mesh.parent?.remove(this.mesh);

    this.disposed = false;
  }

  /** Truly free GPU resources. Only for app teardown — NOT between rounds/rooms. */
  destroy(): void {
    this.disposed = true;
    this.mesh.geometry.dispose();
    this.material.dispose();
    if (this.texture && !this.textureShared) this.texture.dispose();
    this.texture = undefined;
    if (this.body && this.physicsEnabled) {
      this.body.world?.removeBody(this.body);
      this.body = null;
      this.physicsEnabled = false;
    }
  }

  /**
   * Dispose every shared currency-note texture and clear the cache. Call this on
   * full game/session teardown (not between betting rounds — the cache is small
   * and meant to persist for the whole session). Safe to call repeatedly.
   */
  static disposeTextureCache(): void {
    for (const p of billTextureCache.values()) {
      p.then((t) => t.dispose()).catch(() => { /* failed load, nothing to free */ });
    }
    billTextureCache.clear();
  }
}
